/**
 * Predefined test table data for autofill functionality
 * 
 * ⚠️ DEVELOPMENT/TESTING ONLY ⚠️
 * This data is intended for development and testing purposes only.
 * It should NEVER be used in production or by end users.
 */

import { PricingData } from '../../shared/types';

export interface TestTableConfig {
  name: string;
  description: string;
  tiers: number;
  billingPeriods: string[];
  pricingGrid: PricingData['pricingGrid'];
}

/**
 * Predefined data for table1_basic
 * 3 tiers, 2 billing periods (monthly, annually)
 */
export const table1BasicData: TestTableConfig = {
  name: 'table1_basic',
  description: 'Basic pricing table with 3 tiers and 2 billing periods',
  tiers: 3,
  billingPeriods: ['monthly', 'annually'],
  pricingGrid: {
    1: {
      monthly: {
        currentPrice: '9',
        currentLink: 'https://example.com/signup/basic-monthly',
        defaultPrice: '9',
        defaultLink: 'https://example.com/signup/basic-monthly',
        testPrices: [
          { price: '11', link: 'https://example.com/signup/basic-monthly' },
          { price: '13', link: 'https://example.com/signup/basic-monthly' }
        ]
      },
      annually: {
        currentPrice: '7',
        currentLink: 'https://example.com/signup/basic-annually',
        defaultPrice: '7',
        defaultLink: 'https://example.com/signup/basic-annually',
        testPrices: [
          { price: '9', link: 'https://example.com/signup/basic-annually' },
          { price: '11', link: 'https://example.com/signup/basic-annually' }
        ]
      }
    },
    2: {
      monthly: {
        currentPrice: '29',
        currentLink: 'https://example.com/signup/pro-monthly',
        defaultPrice: '29',
        defaultLink: 'https://example.com/signup/pro-monthly',
        testPrices: [
          { price: '35', link: 'https://example.com/signup/pro-monthly' },
          { price: '39', link: 'https://example.com/signup/pro-monthly' }
        ]
      },
      annually: {
        currentPrice: '23',
        currentLink: 'https://example.com/signup/pro-annually',
        defaultPrice: '23',
        defaultLink: 'https://example.com/signup/pro-annually',
        testPrices: [
          { price: '27', link: 'https://example.com/signup/pro-annually' },
          { price: '31', link: 'https://example.com/signup/pro-annually' }
        ]
      }
    },
    3: {
      monthly: {
        currentPrice: '99',
        currentLink: 'https://example.com/signup/enterprise-monthly',
        defaultPrice: '99',
        defaultLink: 'https://example.com/signup/enterprise-monthly',
        testPrices: [
          { price: '119', link: 'https://example.com/signup/enterprise-monthly' },
          { price: '139', link: 'https://example.com/signup/enterprise-monthly' }
        ]
      },
      annually: {
        currentPrice: '79',
        currentLink: 'https://example.com/signup/enterprise-annually',
        defaultPrice: '79',
        defaultLink: 'https://example.com/signup/enterprise-annually',
        testPrices: [
          { price: '95', link: 'https://example.com/signup/enterprise-annually' },
          { price: '115', link: 'https://example.com/signup/enterprise-annually' }
        ]
      }
    }
  }
};

/**
 * Predefined data for table2_basic
 * 4 tiers, 3 billing periods (monthly, quarterly, annually)
 */
export const table2BasicData: TestTableConfig = {
  name: 'table2_basic',
  description: 'Advanced pricing table with 4 tiers and 3 billing periods',
  tiers: 4,
  billingPeriods: ['monthly', 'quarterly', 'annual'],
  pricingGrid: {
    1: {
      monthly: {
        currentPrice: '12',
        currentLink: 'https://example.com/signup/starter',
        defaultPrice: '12',
        defaultLink: 'https://example.com/signup/starter',
        testPrices: [
          { price: '14', link: 'https://example.com/signup/starter' },
          { price: '16', link: 'https://example.com/signup/starter' }
        ]
      },
      quarterly: {
        currentPrice: '11',
        currentLink: 'https://example.com/signup/starter',
        defaultPrice: '11',
        defaultLink: 'https://example.com/signup/starter',
        testPrices: [
          { price: '13', link: 'https://example.com/signup/starter' },
          { price: '15', link: 'https://example.com/signup/starter' }
        ]
      },
      annual: {
        currentPrice: '9',
        currentLink: 'https://example.com/signup/starter',
        defaultPrice: '9',
        defaultLink: 'https://example.com/signup/starter',
        testPrices: [
          { price: '11', link: 'https://example.com/signup/starter' },
          { price: '13', link: 'https://example.com/signup/starter' }
        ]
      }
    },
    2: {
      monthly: {
        currentPrice: '39',
        currentLink: 'https://example.com/signup/professional',
        defaultPrice: '39',
        defaultLink: 'https://example.com/signup/professional',
        testPrices: [
          { price: '45', link: 'https://example.com/signup/professional' },
          { price: '49', link: 'https://example.com/signup/professional' }
        ]
      },
      quarterly: {
        currentPrice: '35',
        currentLink: 'https://example.com/signup/professional',
        defaultPrice: '35',
        defaultLink: 'https://example.com/signup/professional',
        testPrices: [
          { price: '41', link: 'https://example.com/signup/professional' },
          { price: '45', link: 'https://example.com/signup/professional' }
        ]
      },
      annual: {
        currentPrice: '29',
        currentLink: 'https://example.com/signup/professional',
        defaultPrice: '29',
        defaultLink: 'https://example.com/signup/professional',
        testPrices: [
          { price: '35', link: 'https://example.com/signup/professional' },
          { price: '39', link: 'https://example.com/signup/professional' }
        ]
      }
    },
    3: {
      monthly: {
        currentPrice: '89',
        currentLink: 'https://example.com/signup/business',
        defaultPrice: '89',
        defaultLink: 'https://example.com/signup/business',
        testPrices: [
          { price: '99', link: 'https://example.com/signup/business' },
          { price: '109', link: 'https://example.com/signup/business' }
        ]
      },
      quarterly: {
        currentPrice: '80',
        currentLink: 'https://example.com/signup/business',
        defaultPrice: '80',
        defaultLink: 'https://example.com/signup/business',
        testPrices: [
          { price: '90', link: 'https://example.com/signup/business' },
          { price: '100', link: 'https://example.com/signup/business' }
        ]
      },
      annual: {
        currentPrice: '67',
        currentLink: 'https://example.com/signup/business',
        defaultPrice: '67',
        defaultLink: 'https://example.com/signup/business',
        testPrices: [
          { price: '77', link: 'https://example.com/signup/business' },
          { price: '87', link: 'https://example.com/signup/business' }
        ]
      }
    },
    4: {
      monthly: {
        currentPrice: '199',
        currentLink: 'https://example.com/contact-sales',
        defaultPrice: '199',
        defaultLink: 'https://example.com/contact-sales',
        testPrices: [
          { price: '229', link: 'https://example.com/contact-sales' },
          { price: '259', link: 'https://example.com/contact-sales' }
        ]
      },
      quarterly: {
        currentPrice: '179',
        currentLink: 'https://example.com/contact-sales',
        defaultPrice: '179',
        defaultLink: 'https://example.com/contact-sales',
        testPrices: [
          { price: '209', link: 'https://example.com/contact-sales' },
          { price: '239', link: 'https://example.com/contact-sales' }
        ]
      },
      annual: {
        currentPrice: '149',
        currentLink: 'https://example.com/contact-sales',
        defaultPrice: '149',
        defaultLink: 'https://example.com/contact-sales',
        testPrices: [
          { price: '179', link: 'https://example.com/contact-sales' },
          { price: '209', link: 'https://example.com/contact-sales' }
        ]
      }
    }
  }
};

/**
 * Predefined data for table3_enterprise
 * 3 tiers, 3 billing periods (monthly, quarterly, annual)
 * Based on the actual table3_enterprise.html structure
 */
export const table3EnterpriseData: TestTableConfig = {
  name: 'table3_enterprise',
  description: 'Enterprise pricing table with 3 tiers and 3 billing periods',
  tiers: 3,
  billingPeriods: ['monthly', 'quarterly', 'annual'],
  pricingGrid: {
    1: {
      monthly: {
        currentPrice: '25',
        currentLink: 'https://example.com/trial/starter',
        defaultPrice: '25',
        defaultLink: 'https://example.com/trial/starter',
        testPrices: [
          { price: '29', link: 'https://example.com/trial/starter' },
          { price: '33', link: 'https://example.com/trial/starter' }
        ]
      },
      quarterly: {
        currentPrice: '22',
        currentLink: 'https://example.com/trial/starter',
        defaultPrice: '22',
        defaultLink: 'https://example.com/trial/starter',
        testPrices: [
          { price: '26', link: 'https://example.com/trial/starter' },
          { price: '30', link: 'https://example.com/trial/starter' }
        ]
      },
      annual: {
        currentPrice: '18',
        currentLink: 'https://example.com/trial/starter',
        defaultPrice: '18',
        defaultLink: 'https://example.com/trial/starter',
        testPrices: [
          { price: '22', link: 'https://example.com/trial/starter' },
          { price: '26', link: 'https://example.com/trial/starter' }
        ]
      }
    },
    2: {
      monthly: {
        currentPrice: '75',
        currentLink: 'https://example.com/trial/professional',
        defaultPrice: '75',
        defaultLink: 'https://example.com/trial/professional',
        testPrices: [
          { price: '85', link: 'https://example.com/trial/professional' },
          { price: '95', link: 'https://example.com/trial/professional' }
        ]
      },
      quarterly: {
        currentPrice: '65',
        currentLink: 'https://example.com/trial/professional',
        defaultPrice: '65',
        defaultLink: 'https://example.com/trial/professional',
        testPrices: [
          { price: '75', link: 'https://example.com/trial/professional' },
          { price: '85', link: 'https://example.com/trial/professional' }
        ]
      },
      annual: {
        currentPrice: '55',
        currentLink: 'https://example.com/trial/professional',
        defaultPrice: '55',
        defaultLink: 'https://example.com/trial/professional',
        testPrices: [
          { price: '65', link: 'https://example.com/trial/professional' },
          { price: '75', link: 'https://example.com/trial/professional' }
        ]
      }
    },
    3: {
      monthly: {
        currentPrice: '299',
        currentLink: 'https://example.com/enterprise/contact',
        defaultPrice: '299',
        defaultLink: 'https://example.com/enterprise/contact',
        testPrices: [
          { price: '349', link: 'https://example.com/enterprise/contact' },
          { price: '399', link: 'https://example.com/enterprise/contact' }
        ]
      },
      quarterly: {
        currentPrice: '259',
        currentLink: 'https://example.com/enterprise/contact',
        defaultPrice: '259',
        defaultLink: 'https://example.com/enterprise/contact',
        testPrices: [
          { price: '309', link: 'https://example.com/enterprise/contact' },
          { price: '359', link: 'https://example.com/enterprise/contact' }
        ]
      },
      annual: {
        currentPrice: '219',
        currentLink: 'https://example.com/enterprise/contact',
        defaultPrice: '219',
        defaultLink: 'https://example.com/enterprise/contact',
        testPrices: [
          { price: '269', link: 'https://example.com/enterprise/contact' },
          { price: '319', link: 'https://example.com/enterprise/contact' }
        ]
      }
    }
  }
};

/**
 * Map of test table names to their configurations
 */
export const testTableConfigs: Record<string, TestTableConfig> = {
  'table1_basic': table1BasicData,
  'table2_basic': table2BasicData,
  'table3_enterprise': table3EnterpriseData
};

/**
 * Get predefined pricing data for a specific test table
 * 
 * ⚠️ DEVELOPMENT/TESTING ONLY ⚠️
 * This function should only be used in test mode for development purposes.
 */
export function getTestTableData(testTableName: string): TestTableConfig | null {
  const config = testTableConfigs[testTableName];
  if (config) {
    console.log(`🧪 TEST MODE: Using predefined data for ${testTableName}`);
    console.log(`📊 ${config.description}`);
    console.log(`📊 Tiers: ${config.tiers}, Billing Periods: ${config.billingPeriods.join(', ')}`);
  }
  return config || null;
}

/**
 * Convert TestTableConfig to PricingData format
 */
export function convertToPricingData(config: TestTableConfig): PricingData {
  return {
    tiers: config.tiers,
    billingPeriods: config.billingPeriods,
    pricingGrid: config.pricingGrid
  };
}
